import React, { useEffect, useState, useContext, useCallback } from "react";
import api from "../../axios/api.js";
import { useParams, useNavigate } from "react-router-dom";
import PopupMap from "../features/PopupMap.jsx";
import { GlobalContext } from "../../contexts/GlobalContext";
import TreePlantationCoverPic from "../../assets/images/newassets/treeplantationCoverPic.jpg";
import imageCompression from "browser-image-compression";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import ClipLoader from "react-spinners/ClipLoader.js";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Chip,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TagsInput from "react-tagsinput";
import Loader from "../common/Loader.jsx";
import Button from "../common/styled components/Button.jsx";
import CommonGrid from "../common/Grid.jsx";
import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaTree,
  FaCloud,
  FaWind,
  FaBullseye,
  FaSave,
  FaCamera,
} from "react-icons/fa";
import {
  CO2_TO_O2_RATIO,
  getSpeciesLabel,
  getTreeCO2e,
} from "../../utils";

const ProjectDetailDashboard = () => {
  const {
    address,
    setAddress,
    latlng,
    setLatlng,
    country,
    addressData,
    role,
    userId,
    isLoggedIn,
  } = useContext(GlobalContext);

  const navigate = useNavigate();
  const { id } = useParams();

  const [project, setProject] = useState({});
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [organizationsOptions, setOrganizationsOptions] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [invalidFields, setInvalidFields] = useState([]);

  // Species & Trees distribution state
  const [speciesData, setSpeciesData] = useState({});
  const [treesLoading, setTreesLoading] = useState(false);

  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    component: null,
  });

  const [esgOptions] = useState([
    { index: 1, value: "No Poverty", label: "No Poverty" },
    { index: 2, value: "Zero Hunger", label: "Zero Hunger" },
    { index: 3, value: "Good Health And Well Being", label: "Good Health And Well Being" },
    { index: 4, value: "Quality Education", label: "Quality Education" },
    { index: 5, value: "Gender Equality", label: "Gender Equality" },
    { index: 6, value: "Clean Water And Sanitation", label: "Clean Water And Sanitation" },
    { index: 7, value: "Affordable And Clean Energy", label: "Affordable And Clean Energy" },
    { index: 8, value: "Decent Work And Economic Growth", label: "Decent Work And Economic Growth" },
    { index: 9, value: "Industry-Innovation and Infrastructure", label: "Industry, Innovation and Infrastructure" },
    { index: 10, value: "Reduced Inequalities", label: "Reduced Inequalities" },
    { index: 11, value: "Sustainable Cities And Communities", label: "Sustainable Cities And Communities" },
    { index: 12, value: "Responsible Consumption And Production", label: "Responsible Consumption And Production" },
    { index: 13, value: "Climate Action", label: "Climate Action" },
    { index: 14, value: "Life Below Water", label: "Life Below Water" },
    { index: 15, value: "Life On Land", label: "Life On Land" },
    { index: 16, value: "Peace-Justice and Strong Institutions", label: "Peace, Justice and Strong Institutions" },
    { index: 17, value: "Partnerships For The Goals", label: "Partnerships For The Goals" },
  ]);

  const openModal = (title, component) => {
    setModalConfig({
      isOpen: true,
      title,
      component,
    });
  };

  const closeModal = () => {
    setModalConfig({
      isOpen: false,
      title: "",
      component: null,
    });
  };

  const renderComponent = () => {
    const Component = modalConfig.component;
    return Component ? <Component onClose={closeModal} /> : null;
  };

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 1,
      useWebWorker: true,
    };
    return await imageCompression(file, options);
  };

  // Process trees to aggregate species distribution and CO2/O2 calculations
  const processSpeciesData = useCallback((trees) => {
    if (!trees || trees.length === 0) {
      setSpeciesData({});
      return;
    }
    const now = new Date();
    const speciesGroups = trees.reduce((acc, tree) => {
      const species = getSpeciesLabel(tree);
      if (!acc[species]) {
        acc[species] = {
          count: 0,
          icon:
            tree.species_icon_url ||
            tree.icon_url ||
            tree.treeImageUrl ||
            tree.treeimageUrl ||
            "/images/icon/leaf.png",
          speciesId: tree.tree_species,
          co2: 0,
          o2: 0,
        };
      }
      acc[species].count++;
      const treeCO2 = getTreeCO2e(tree, now);
      acc[species].co2 += treeCO2;
      acc[species].o2 += treeCO2 * CO2_TO_O2_RATIO;
      return acc;
    }, {});
    setSpeciesData(speciesGroups);
  }, []);

  // Fetch trees and species details for the project
  const fetchTreesAndSpecies = useCallback(async () => {
    if (!id) return;
    setTreesLoading(true);
    try {
      const response = await api.get(`/api/project/getProjectGalleryDetails/${id}`);
      if (response.data && response.data.Trees) {
        processSpeciesData(response.data.Trees);
      }
    } catch (error) {
      console.error("Error fetching project trees:", error);
    } finally {
      setTreesLoading(false);
    }
  }, [id, processSpeciesData]);

  // Fetch project details & organizations
  const fetchProjectDetails = useCallback(
    async (orgOptions) => {
      try {
        const response = await api.get(`/api/project/getProjectEditDetails/${id}`);
        const projectData = response.data.project;
        setProject(projectData);
        setCoverPhotoPreview(projectData.project_cover_image);
        setAddress(projectData.address || "");
        if (projectData.lat && projectData.longi) {
          setLatlng({ lat: projectData.lat, lng: projectData.longi });
        }

        const parsedTags = projectData.tags ? projectData.tags.split(",") : [];
        setTags(parsedTags);

        // ESG Goals
        const esgGoalsArray = projectData.esgGoals
          ? projectData.esgGoals.split(",")
          : [];
        const selectedG = esgGoalsArray
          .map((goal) => esgOptions.find((option) => option.value === goal))
          .filter(Boolean);
        setSelectedGoals(selectedG);

        // Organizations
        const companyIdsArray = projectData.companyIds
          ? projectData.companyIds.split(",")
          : [];
        const selectedO = companyIdsArray
          .map((companyId) =>
            orgOptions.find((option) => option.value.toString() === companyId)
          )
          .filter(Boolean);
        setSelectedOrganizations(selectedO);
      } catch (error) {
        console.error("Error fetching project details:", error);
        toast.error("Failed to load project details");
      }
    },
    [id, setAddress, setLatlng, esgOptions]
  );

  useEffect(() => {
    const fetchOrganizationsAndProject = async () => {
      setIsLoading(true);
      try {
        const orgResponse = await api.get("/api/user/organizations");
        let orgOptions = [];
        if (orgResponse.data && orgResponse.data.length > 0) {
          orgOptions = orgResponse.data[0].map((org) => ({
            value: org.id,
            label: org.company_name,
            created_by_user: org.created_by_user,
          }));

          const filteredOptions =
            role === "corporate" || role === "ngo"
              ? orgOptions.filter(
                  (organization) => organization.created_by_user === userId
                )
              : role === "admin"
              ? orgOptions
              : orgOptions;

          setOrganizationsOptions(filteredOptions);
          await fetchProjectDetails(filteredOptions);
        } else {
          await fetchProjectDetails([]);
        }

        // Fetch species distribution
        await fetchTreesAndSpecies();
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizationsAndProject();
  }, [role, userId, id, fetchProjectDetails, fetchTreesAndSpecies]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "target" || name === "listing_order") {
      const nodecimalregex = /^\d*$/;
      if (nodecimalregex.test(value)) {
        setProject((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setProject((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCoverPicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverPhoto(file);
      setCoverPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!isLoggedIn) {
      toast.error("Please log in to save changes");
      setTimeout(() => navigate("/login"), 1000);
      return;
    }

    const invalids = [];
    if (!project.name) invalids.push("projectName");
    if (!project.startDate) invalids.push("startDate");
    if (!project.endDate) invalids.push("endDate");
    if (!project.description) invalids.push("Desc");
    if (!project.target) invalids.push("target");
    if (!address) invalids.push("address");
    if (selectedOrganizations.length === 0) invalids.push("Orgs");
    if (selectedGoals.length === 0) invalids.push("goals");
    if (!coverPhoto && !coverPhotoPreview) invalids.push("cover");
    if (tags.length === 0) invalids.push("tags");

    setInvalidFields(invalids);

    if (invalids.length > 0) {
      toast.error("Please fill in all mandatory fields.");
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();

      if (coverPhoto) {
        const compressedCover = await compressImage(coverPhoto);
        formData.append("projectCoverImage", compressedCover);
        formData.append("coverImageName", coverPhoto.name);
      }

      const sortedGoals = selectedGoals.sort((a, b) => a.index - b.index);

      Object.keys(project).forEach((key) => {
        formData.append(key, project[key]);
      });

      if (latlng) {
        formData.set("lat", latlng.lat);
        formData.set("longi", latlng.lng);
      }
      if (addressData) {
        formData.append("city", addressData.city || "");
        formData.append("state", addressData.state || "");
        formData.append("post_code", addressData.postcode || "");
      }
      formData.append("country", country || "");
      formData.set("address", address);
      formData.set("tags", tags);
      formData.append("listing_order", project.listing_order || 0);
      formData.set("esgGoals", sortedGoals.map((val) => val.value));
      formData.set("companyIds", selectedOrganizations.map((val) => val.value));
      formData.set("organizationName", selectedOrganizations.map((val) => val.label));

      await api.post(`/api/project/updateProject/${project.project_id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success("Project updated successfully!");
      setTimeout(() => {
        navigate("/dashboard/projectdashboard");
      }, 800);
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error(error.response?.data?.message || "Error updating project.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getInvalidStyle = (fieldName) =>
    invalidFields.includes(fieldName)
      ? "border-2 border-red-500 bg-red-50/20"
      : "border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200";

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "42px",
      borderRadius: "0.5rem",
      borderColor: state.isFocused ? "#10b981" : "#e2e8f0",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(16, 185, 129, 0.2)" : "none",
      "&:hover": { borderColor: "#cbd5e1" },
    }),
    singleValue: (base) => ({ ...base, color: "#1e293b" }),
    placeholder: (base) => ({ ...base, color: "#94a3b8" }),
  };

  // Metrics summary calculations
  const totalSpeciesCount = Object.keys(speciesData).length;
  const totalTreesCount = Object.values(speciesData).reduce(
    (sum, i) => sum + i.count,
    0
  );
  const totalCo2Val = Object.values(speciesData).reduce(
    (sum, i) => sum + (i.co2 || 0),
    0
  );
  const totalO2Val = Object.values(speciesData).reduce(
    (sum, i) => sum + (i.o2 || 0),
    0
  );
  const totalCo2Str =
    totalCo2Val >= 1000
      ? `${(totalCo2Val / 1000).toFixed(2)} t`
      : `${totalCo2Val.toFixed(2)} kg`;
  const totalO2Str =
    totalO2Val >= 1000
      ? `${(totalO2Val / 1000).toFixed(2)} t`
      : `${totalO2Val.toFixed(2)} kg`;

  return (
    <div className="min-h-screen bg-slate-50/60 pb-16">
      <ToastContainer position="top-right" autoClose={3000} />

      {isLoading && (
        <div className="fixed inset-0 bg-white/70 backdrop-blur-xs z-50 flex justify-center items-center">
          <Loader />
        </div>
      )}

      {/* Location Picker Modal */}
      <Dialog
        open={modalConfig.isOpen}
        onClose={closeModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2, fontWeight: 700, color: "#002F43" }}>
          {modalConfig.title}
          <IconButton
            onClick={closeModal}
            sx={{ position: "absolute", right: 8, top: 8, color: "#64748b" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {renderComponent()}
        </DialogContent>
      </Dialog>

      <div className="max-w-6xl m-auto px-4 sm:px-6 pt-6" style={{paddingBottom:"10px"}}>
        {/* Top Header Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="gray"
              onClick={() => navigate("/dashboard/projectdashboard")}
            >
              <FaArrowLeft size={12} style={{ marginRight: "6px" }} /> Back to Projects
            </Button>

            <div className="flex items-center gap-2">
              <div className="w-1.5 h-7 bg-emerald-500 rounded-full" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-[#002F43] tracking-tight m-0">
                  {project.name || "Project Dashboard"}
                </h1>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2" style={{color:"black"}}>
            <Chip
              label={
                project.is_active === "0"
                  ? "Inactive Project"
                  : "Active Project"
              }
              size="small"
              sx={{
                color: project.is_active === "0" ? "#fee2e2" : "#dcfce7",
                bgcolor: project.is_active === "0" ? "#b91c1c" : "#15803d",
                fontWeight: 600,
              }}
            />
          </div>
        </div>

        {/* 4 Stat Overview Badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5 mb-6">
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
              <FaTree size={18} />
            </div>
            <div>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Planted / Target
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                {project.total_trees_planted || totalTreesCount || 0}{" "}
                <span className="text-xs text-slate-400 font-normal">
                  / {project.target || 0}
                </span>
              </Typography>
            </div>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
              <FaCloud size={18} />
            </div>
            <div>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                CO₂ Sequestration
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                {totalCo2Str}
              </Typography>
            </div>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <div className="w-10 h-10 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center shrink-0">
              <FaWind size={18} />
            </div>
            <div>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                O2 Produced
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                {totalO2Str}
              </Typography>
            </div>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: "1px solid #e2e8f0",
              bgcolor: "#ffffff",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
              <FaBullseye size={18} />
            </div>
            <div>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Total Species
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                {totalSpeciesCount} Species
              </Typography>
            </div>
          </Paper>
        </div>

        {/* Project Details Form */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3.5 },
            borderRadius: 3,
            border: "1px solid #e2e8f0",
            bgcolor: "#ffffff",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.03)",
            mb: 5,
          }}
        >
          <div className="flex flex-col gap-5">
            {/* Cover Picture & Basic Details Header */}
            <div className="flex flex-col lg:flex-row gap-5 items-start">
              {/* Cover Photo Upload Box */}
              <div className="w-full lg:w-72 shrink-0">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                  Project Cover Image <span className="text-red-500">*</span>
                </label>
                <div
                  className={`relative w-full h-44 rounded-xl overflow-hidden cursor-pointer group border-2 ${
                    invalidFields.includes("cover")
                      ? "border-red-500 bg-red-50"
                      : "border-dashed border-slate-300 hover:border-emerald-500 bg-slate-50"
                  } transition-all`}
                  onClick={() =>
                    document.getElementById("cover-upload-input")?.click()
                  }
                >
                  <img
                    src={coverPhotoPreview || TreePlantationCoverPic}
                    alt="Cover Pic"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/35 group-hover:bg-black/50 transition-colors flex flex-col items-center justify-center text-white p-2">
                    <FaCamera size={20} className="mb-1 opacity-90" />
                    <span className="text-xs font-semibold">Change Cover Image</span>
                  </div>
                  <input
                    id="cover-upload-input"
                    type="file"
                    accept="image/*"
                    onChange={handleCoverPicChange}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Main Information Inputs */}
              <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Project Name */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Project Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={project.name || ""}
                    onChange={handleChange}
                    placeholder="Enter project name..."
                    className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-slate-800 outline-none transition-all ${getInvalidStyle(
                      "projectName"
                    )}`}
                  />
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={project.startDate || ""}
                    onChange={handleChange}
                    className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-slate-800 outline-none transition-all ${getInvalidStyle(
                      "startDate"
                    )}`}
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={project.endDate || ""}
                    onChange={handleChange}
                    className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-slate-800 outline-none transition-all ${getInvalidStyle(
                      "endDate"
                    )}`}
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Project Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={project.description || ""}
                onChange={handleChange}
                placeholder="Detailed project summary, plantation objectives, and background..."
                rows={4}
                className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-slate-800 outline-none transition-all resize-y min-h-[100px] ${getInvalidStyle(
                  "Desc"
                )}`}
              />
            </div>

            {/* Target, Listing Order, Organizations, ESG Goals */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Tree Target <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="target"
                  value={project.target || ""}
                  onChange={handleChange}
                  placeholder="e.g. 5000"
                  className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-slate-800 outline-none transition-all ${getInvalidStyle(
                    "target"
                  )}`}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Listing Order
                </label>
                <input
                  type="number"
                  name="listing_order"
                  value={project.listing_order || ""}
                  onChange={handleChange}
                  placeholder="e.g. 1"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 text-sm text-slate-800 outline-none transition-all"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Organizations / Companies <span className="text-red-500">*</span>
                </label>
                <Select
                  options={organizationsOptions}
                  isMulti
                  value={selectedOrganizations}
                  onChange={(opts) => setSelectedOrganizations(opts || [])}
                  styles={selectStyles}
                  placeholder="Select participating companies..."
                />
              </div>
            </div>

            {/* ESG Goals */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Project ESG Goals <span className="text-red-500">*</span>
              </label>
              <Select
                options={esgOptions}
                isMulti
                value={selectedGoals}
                onChange={(opts) => setSelectedGoals(opts || [])}
                styles={selectStyles}
                placeholder="Select UN SDGs & ESG goals..."
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Project Tags <span className="text-red-500">*</span>
              </label>
              <div
                className={`rounded-lg border p-1.5 transition-all ${
                  invalidFields.includes("tags")
                    ? "border-red-500 bg-red-50/20"
                    : "border-gray-200"
                }`}
              >
                <TagsInput
                  value={tags}
                  onChange={setTags}
                  className="react-tagsinput"
                  tagProps={{
                    className:
                      "react-tagsinput-tag bg-[#002F43] text-white rounded px-2.5 py-1 m-1 text-xs font-medium inline-flex items-center gap-1.5",
                    removeButtonStyle: {
                      fontSize: "14px",
                      color: "#94a3b8",
                      cursor: "pointer",
                    },
                  }}
                  inputProps={{
                    className: "react-tagsinput-input text-sm p-1 outline-none",
                    placeholder: "Add a tag...",
                  }}
                />
              </div>
            </div>

            {/* Location with Map Modal Picker */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                Project Location &amp; Coordinates <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Select location coordinates from map..."
                  value={address || ""}
                  readOnly
                  className={`flex-1 px-3.5 py-2.5 rounded-lg border text-sm text-slate-800 bg-slate-50 cursor-pointer outline-none ${getInvalidStyle(
                    "address"
                  )}`}
                  onClick={() => openModal("Select Project Location", PopupMap)}
                />
                <button
                  type="button"
                  onClick={() => openModal("Select Project Location", PopupMap)}
                  className="px-4 py-2 bg-[#002F43] hover:bg-[#001d2b] text-white rounded-lg flex items-center gap-2 text-xs font-semibold cursor-pointer shadow-xs transition-colors"
                >
                  <FaMapMarkerAlt size={14} /> Map
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
              <Button
                variant="cancel"
                onClick={() => navigate("/dashboard/projectdashboard")}
                disabled={isSubmitting}
              >
                Cancel
              </Button>

              <Button
                type="button"
                disabled={isSubmitting}
                onClick={handleSave}
              >
                {isSubmitting ? (
                  <ClipLoader color="white" size={16} />
                ) : (
                  <>
                    <FaSave size={13} style={{ marginRight: "6px" }} /> Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
        </Paper>

        {/* Species-wise Tree Distribution Table (Same as ProjectPage with metrics) */}
        <div className="mt-8 mb-12">
          {/* Table Header with badges */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-1 h-6 bg-emerald-500 rounded-full" />
              <h2 className="text-lg sm:text-xl font-bold text-[#002F43] m-0 tracking-tight">
                Species-wise Tree Distribution
              </h2>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                Total Species: {totalSpeciesCount}
              </span>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-50 text-teal-800 border border-teal-200">
                Trees Count: {totalTreesCount}
              </span>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-cyan-50 text-cyan-800 border border-cyan-200">
                CO₂ Sequestration: {totalCo2Str}
              </span>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-800 border border-blue-200">
                O2 Produce: {totalO2Str}
              </span>
            </div>
          </div>

          <CommonGrid
            columns={[
              {
                header: "Sr. No.",
                width: "80px",
                align: "center",
                render: (_, __, index) => index + 1,
              },
              {
                header: "Species Name",
                accessor: "speciesName",
                sortable: true,
                align: "left",
                render: (_, item) => (
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "50%",
                        overflow: "hidden",
                        backgroundColor: "#f1f5f9",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <img
                        src={item.icon || "/images/icon/leaf.png"}
                        alt={item.speciesName}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/images/icon/leaf.png";
                        }}
                      />
                    </div>
                    <span style={{ fontWeight: 600, color: "#0f172a" }}>
                      {item.speciesName}
                    </span>
                  </div>
                ),
              },
              {
                header: "Tree Count",
                accessor: "count",
                sortable: true,
                align: "center",
                render: (val) => (
                  <span
                    style={{
                      display: "inline-block",
                      padding: "3px 10px",
                      borderRadius: "12px",
                      backgroundColor: "#e2e8f0",
                      color: "#1e293b",
                      fontWeight: 700,
                      fontSize: "12px",
                    }}
                  >
                    {val}
                  </span>
                ),
              },
              {
                header: "CO₂ Sequestration",
                accessor: "co2",
                sortable: true,
                align: "right",
                render: (val) => {
                  const co2Formatted =
                    val >= 1000
                      ? `${(val / 1000).toFixed(2)} t`
                      : `${(val || 0).toFixed(2)} kg`;
                  return (
                    <span
                      style={{
                        display: "inline-block",
                        padding: "3px 10px",
                        borderRadius: "12px",
                        backgroundColor: "#ecfdf5",
                        color: "#047857",
                        fontWeight: 700,
                        fontSize: "12px",
                      }}
                    >
                      {co2Formatted}
                    </span>
                  );
                },
              },
              {
                header: "O2 Produce",
                accessor: "o2",
                sortable: true,
                align: "right",
                render: (val) => {
                  const o2Formatted =
                    val >= 1000
                      ? `${(val / 1000).toFixed(2)} t`
                      : `${(val || 0).toFixed(2)} kg`;
                  return (
                    <span
                      style={{
                        display: "inline-block",
                        padding: "3px 10px",
                        borderRadius: "12px",
                        backgroundColor: "#f0fdf4",
                        color: "#15803d",
                        fontWeight: 700,
                        fontSize: "12px",
                      }}
                    >
                      {o2Formatted}
                    </span>
                  );
                },
              },
            ]}
            data={Object.keys(speciesData)
              .sort((a, b) => a.localeCompare(b))
              .map((key) => ({
                speciesName: key,
                ...speciesData[key],
              }))}
            loading={treesLoading}
            variant="navy"
            pagination={true}
            pageSize={10}
            pageSizeOptions={[5, 10, 20, 50]}
            rowKey="speciesName"
            emptyMessage="No species data recorded for this project yet."
            footer={[
              { content: "" },
              {
                content: (
                  <span style={{ fontWeight: 700, letterSpacing: "0.5px",color:"#002F43" }}>
                    TOTAL
                  </span>
                ),
              },
              {
                content: (
                  <span
                    style={{
                      display: "inline-block",
                      padding: "3px 10px",
                      borderRadius: "12px",
                      backgroundColor: "#002F43",
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "12px",
                    }}
                  >
                    {totalTreesCount}
                  </span>
                ),
                align: "center",
              },
              {
                content: (
                  <span
                    style={{
                      display: "inline-block",
                      padding: "3px 10px",
                      borderRadius: "12px",
                      backgroundColor: "#047857",
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "12px",
                    }}
                  >
                    {totalCo2Str}
                  </span>
                ),
                align: "right",
              },
              {
                content: (
                  <span
                    style={{
                      display: "inline-block",
                      padding: "3px 10px",
                      borderRadius: "12px",
                      backgroundColor: "#15803d",
                      color: "#ffffff",
                      fontWeight: 700,
                      fontSize: "12px",
                    }}
                  >
                    {totalO2Str}
                  </span>
                ),
                align: "right",
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailDashboard;
